import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { apiClient, logout } from '../../shared/services/api';
import { trackEvent } from '../../shared/utils/analytics';
import { getLocalReferrerUpgradeRequest, clearLocalReferrerUpgradeRequest } from '../../shared/utils/referrerUpgradeStorage';
import { buildUiAvatarsUrl, resolveProfileDisplayName } from '../../shared/utils/profileDisplay';
import { isProfileVerified } from '../../shared/utils/profileVerified';
import { isWorkingProfessionalUser, syncUserRoleFromProfile } from '../../shared/utils/syncUserRole';
import { applyTheme } from '../../shared/utils/dashboardTheme';
import { prefetchAllDashboardChunks } from '../../app/routes/dashboardChunkPrefetch';
import { PreLaunchNoticeModal } from '../../shared/components/common/PreLaunchNoticeModal';
import { HiredUpgradeModal } from './HiredUpgradeModal';
import { LogoutConfirmationModal } from './LogoutConfirmationModal';
import { DashboardSidebar, DashboardTopbar } from './DashboardChrome';
import { MobileTabBar } from './MobileTabBar';
import {
  buildCandidateTrustNav,
  buildEmployeeAsJobseekerNav,
  buildJobSeekerNav,
  buildProfessionalTrustNav,
  EMPLOYEE_NAV,
  findNavLabel,
  type NavEntry,
} from './navConfig';
import { usePlatformFeatures } from '../../shared/hooks/usePlatformFeatures';
import { useTrustFlags } from '../../features/career-trust/hooks/useTrustFlags';
import { useIsMobileViewport } from '../../shared/hooks/useMediaQuery';
import { Skeleton, SkeletonGroup } from '../../shared/ui';
import { ProductTour } from '../../shared/tour';
import './DashboardLayout.css';
import './dashboard-theme.css';
import './dashboard-typography.css';
import '../../shared/layout/app-layout.css';
import '../../shared/layout/app-layout-normalize.css';

type DashboardMode = 'jobseeker' | 'employee';

function readUserData() {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

function ContentLoader() {
  return (
    <div className="dash-content-loader" role="status" aria-label="Loading page">
      <SkeletonGroup lines={2} className="dash-content-skeleton" />
      <div className="dash-content-skeleton__cards" aria-hidden="true">
        <Skeleton variant="card" />
        <Skeleton variant="card" />
        <Skeleton variant="card" />
      </div>
    </div>
  );
}

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLElement>(null);
  const [userData, setUserData] = useState<ReturnType<typeof readUserData>>(() => readUserData());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobileViewport = useIsMobileViewport();
  const effectiveSidebarCollapsed = sidebarCollapsed && !isMobileViewport;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [hiredModalOpen, setHiredModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [upgradeRequestStatus, setUpgradeRequestStatus] = useState<string | null>(null);
  const [tourRequestId, setTourRequestId] = useState(0);

  const [showPreLaunchModal, setShowPreLaunchModal] = useState<boolean>(() => {
    return localStorage.getItem('show_wp_prelaunch_modal') === 'true';
  });

  const handleStartTour = useCallback(() => setTourRequestId((n) => n + 1), []);

  useEffect(() => {
    applyTheme('dark');
  }, []);

  useEffect(() => {
    prefetchAllDashboardChunks();
  }, []);

  useEffect(() => {
    const syncUserFromStorage = () => setUserData(readUserData());
    window.addEventListener('storage', syncUserFromStorage);
    window.addEventListener('user-profile-updated', syncUserFromStorage);
    return () => {
      window.removeEventListener('storage', syncUserFromStorage);
      window.removeEventListener('user-profile-updated', syncUserFromStorage);
    };
  }, []);

  useEffect(() => {
    setMobileSidebarOpen(false);
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setSearchQuery('');
    setSearchModalOpen(false);
  }, [location.pathname]);

  // Lock background scroll while the mobile drawer is open
  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileSidebarOpen]);

  const refreshUserData = useCallback(() => {
    setUserData(readUserData());
  }, []);

  useEffect(() => {
    if (!userData) return;
    const isWorkingProf = isWorkingProfessionalUser(userData);
    if (!isWorkingProf && location.pathname.startsWith('/referral-dashboard')) {
      navigate('/jobseeker-dashboard', { replace: true });
    }
  }, [location.pathname, userData, navigate]);

  useEffect(() => {
    let cancelled = false;

    const checkRoleAndRequest = async () => {
      const upgraded = await syncUserRoleFromProfile();
      if (cancelled) return;

      if (upgraded) {
        refreshUserData();
        localStorage.setItem('activeDashboardMode', 'employee');
        navigate('/referral-dashboard', { replace: true });
        return;
      }

      refreshUserData();

      if (!isWorkingProfessionalUser(readUserData())) {
        const result = await apiClient.getMyReferrerUpgradeRequest();
        if (cancelled) return;

        if (result.success) {
          if (result.data?.status) {
            setUpgradeRequestStatus(result.data.status);
            const localReq = getLocalReferrerUpgradeRequest();
            if (localReq) {
              localReq.status = result.data.status;
              localStorage.setItem('referrer_upgrade_request', JSON.stringify(localReq));
            }
          } else {
            setUpgradeRequestStatus(null);
            clearLocalReferrerUpgradeRequest();
          }
        } else {
          const localReq = getLocalReferrerUpgradeRequest();
          if (localReq?.status) {
            setUpgradeRequestStatus(localReq.status);
          }
        }
      }
    };

    void checkRoleAndRequest();
    return () => {
      cancelled = true;
    };
  }, [navigate, refreshUserData]);

  const dashboardMode: DashboardMode = useMemo(() => {
    const isWP =
      userData?.userType === 'working_professional' ||
      userData?.user_type === 'working_professional' ||
      userData?.role === 'referrer';

    if (location.pathname.startsWith('/referral-dashboard/jobseeker-dashboard')) {
      localStorage.setItem('activeDashboardMode', 'jobseeker');
      return 'jobseeker';
    }

    if (location.pathname.startsWith('/referral-dashboard')) {
      localStorage.setItem('activeDashboardMode', 'employee');
      return 'employee';
    }

    if (
      location.pathname.startsWith('/jobseeker-dashboard') ||
      location.pathname.startsWith('/booking-confirmed') ||
      location.pathname.startsWith('/rate-review')
    ) {
      localStorage.setItem('activeDashboardMode', 'jobseeker');
      return 'jobseeker';
    }

    const savedMode = localStorage.getItem('activeDashboardMode') as DashboardMode | null;
    if (savedMode === 'employee' || isWP) {
      return 'employee';
    }

    return 'jobseeker';
  }, [location.pathname, userData]);

  const { enabledFeatures } = usePlatformFeatures();
  const { flags: trustFlags } = useTrustFlags();

  const navEntries: NavEntry[] = useMemo(() => {
    if (dashboardMode === 'employee') {
      // Professional dashboard: append the verification inbox when enabled.
      return [...EMPLOYEE_NAV, ...buildProfessionalTrustNav(trustFlags)];
    }
    if (isWorkingProfessionalUser(userData)) {
      return [
        ...buildEmployeeAsJobseekerNav(enabledFeatures),
        ...buildCandidateTrustNav(trustFlags, '/referral-dashboard/jobseeker-dashboard'),
      ];
    }
    return [
      ...buildJobSeekerNav(enabledFeatures),
      ...buildCandidateTrustNav(trustFlags, '/jobseeker-dashboard'),
    ];
  }, [dashboardMode, enabledFeatures, trustFlags, userData]);

  const userName = resolveProfileDisplayName(userData ?? {});
  const userEmail = (userData?.primary_email || userData?.email || '').trim();
  const avatarSeed = userName || userEmail;
  const userAvatar =
    userData?.avatar ||
    userData?.profile_picture ||
    buildUiAvatarsUrl(avatarSeed);

  const handleSwitchMode = useCallback(
    (mode: DashboardMode) => {
      localStorage.setItem('activeDashboardMode', mode);
      if (mode === 'employee') {
        navigate('/referral-dashboard');
      } else {
        navigate('/referral-dashboard/jobseeker-dashboard');
      }
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    setLogoutModalOpen(true);
  }, []);

  const handleConfirmLogout = useCallback(() => {
    setLogoutModalOpen(false);
    trackEvent('logout');
    logout();
  }, []);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/referral-dashboard' || path === '/jobseeker-dashboard') {
        return location.pathname === path;
      }
      if (
        path === '/referral-dashboard/analytics' &&
        location.pathname === '/referral-dashboard/analytics/withdrawal'
      ) {
        return true;
      }
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    },
    [location.pathname]
  );

  const pageTitle = findNavLabel(navEntries, location.pathname, isActive);
  const userVerified = isProfileVerified(userData);

  const normalizedPath =
    location.pathname.length > 1 ? location.pathname.replace(/\/+$/, '') : location.pathname;
  const showTopbarSearch = [
    '/jobseeker-dashboard',
    '/referral-dashboard',
    '/referral-dashboard/jobseeker-dashboard',
  ].includes(normalizedPath);

  const isOnJobSeekerDashboard =
    location.pathname === '/jobseeker-dashboard' ||
    location.pathname.startsWith('/jobseeker-dashboard/');

  const showHiredButton = isOnJobSeekerDashboard && upgradeRequestStatus !== 'approved';
  const hiredButtonLabel = upgradeRequestStatus === 'pending' ? 'Request pending' : "I'm hired";

  const showSidebarUpgradeCard =
    isOnJobSeekerDashboard && upgradeRequestStatus !== 'approved' && !effectiveSidebarCollapsed;

  const handleHiredSubmitted = useCallback(() => {
    setUpgradeRequestStatus('pending');
  }, []);

  const openProfile = useCallback(() => {
    if (dashboardMode === 'employee') {
      navigate('/referral-dashboard/profile');
    } else if (isWorkingProfessionalUser(userData)) {
      navigate('/referral-dashboard/jobseeker-dashboard/profile');
    } else {
      navigate('/jobseeker-dashboard/profile');
    }
  }, [dashboardMode, userData, navigate]);

  const openSettings = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  return (
    <div
      className={`dash-layout ${effectiveSidebarCollapsed ? 'dash-layout--collapsed' : ''}`}
      data-dash-theme="dark"
    >
      {mobileSidebarOpen && (
        <div className="dash-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <DashboardSidebar
        sidebarCollapsed={effectiveSidebarCollapsed}
        mobileSidebarOpen={mobileSidebarOpen}
        showReferralSwitcher={isWorkingProfessionalUser(userData)}
        dashboardMode={dashboardMode}
        navEntries={navEntries}
        isActive={isActive}
        showSidebarUpgradeCard={showSidebarUpgradeCard}
        upgradeRequestStatus={upgradeRequestStatus}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        onSwitchMode={handleSwitchMode}
        onLogout={handleLogout}
        onOpenUpgrade={() => setHiredModalOpen(true)}
        onMouseEnter={undefined}
        onMouseLeave={undefined}
      />

      <div className="dash-main">
        <DashboardTopbar
          pageTitle={pageTitle}
          showTopbarSearch={showTopbarSearch}
          showHiredButton={showHiredButton}
          hiredButtonLabel={hiredButtonLabel}
          upgradeRequestStatus={upgradeRequestStatus}
          searchQuery={searchQuery}
          userName={userName}
          userEmail={userEmail}
          userAvatar={userAvatar}
          userVerified={userVerified}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
          onSearchChange={setSearchQuery}
          onOpenHiredModal={() => setHiredModalOpen(true)}
          onOpenProfile={openProfile}
          onOpenSettings={openSettings}
          onStartTour={handleStartTour}
          onSearchClick={dashboardMode === 'jobseeker' ? () => setSearchModalOpen(true) : undefined}
        />

        <main className="dash-content" ref={contentRef}>
          <Suspense fallback={<ContentLoader />}>
            <Outlet context={{ searchQuery, setSearchQuery, searchModalOpen, setSearchModalOpen }} />
          </Suspense>
        </main>
      </div>

      {isMobileViewport && (
        <MobileTabBar
          navEntries={navEntries}
          isActive={isActive}
          onOpenMenu={() => setMobileSidebarOpen(true)}
        />
      )}
      <ProductTour mode={dashboardMode} requestId={tourRequestId} />

      <HiredUpgradeModal
        isOpen={hiredModalOpen}
        onClose={() => setHiredModalOpen(false)}
        onSubmitted={handleHiredSubmitted}
      />

      <LogoutConfirmationModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleConfirmLogout}
      />

      <PreLaunchNoticeModal
        isOpen={showPreLaunchModal}
        onClose={() => {
          localStorage.removeItem('show_wp_prelaunch_modal');
          setShowPreLaunchModal(false);
        }}
      />
    </div>
  );
}

export default DashboardLayout;
